import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Map as MaplibreMap, NavigationControl, Marker, setWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { activityGpxUrl } from '../../api/client';
import { getToken } from '../../auth/authStore';
import { parseGpxTrack } from '../AddActivityModal/parseGpx';
import './GpxTrackView.css';

// CRA's webpack config can't resolve maplibre-gl's own module-worker bundle
// as an import (it 404s to index.html, "Worker failed to load"), and loading
// it from a CDN hits a separate webpack worker-loader context error since
// it's cross-origin. `scripts/copy-maplibre-worker.js` copies the file
// (matching the installed maplibre-gl version) into public/ on
// install/start/build, so it's just served same-origin like any other
// static asset.
setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');

// Same free, key-less satellite raster used by the main map (RouteMap.jsx).
const SATELLITE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
// Mapzen/AWS Open Data terrain-RGB tiles (Terrarium encoding) — public, no key,
// used here so the map has real elevation to tilt/rotate over.
const TERRAIN_URL = 'https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png';

function trackBounds(points) {
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  points.forEach((p) => {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  });
  return [[minLon, minLat], [maxLon, maxLat]];
}

/**
 * Below-the-description GPX section for the senderismo/ciclismo detail pages:
 * a rotatable 3D map of the track next to its elevation profile, hovering
 * either one highlights the matching point on the other. Deliberately shows
 * no distance/elevation/duration numbers — those already live in the stats
 * tiles elsewhere on the page; this is purely the visual track + profile.
 * Renders nothing if the activity has no GPX file, or the file has no usable
 * track/elevation data.
 */
function GpxTrackView({ gpxFileId }) {
  const [track, setTrack] = useState(null); // { points, waypoints, hasElevation }
  const [failed, setFailed] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);

  useEffect(() => {
    if (!gpxFileId) return;
    let alive = true;
    const token = getToken();
    fetch(activityGpxUrl(gpxFileId), {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => {
        if (!res.ok) throw new Error('gpx fetch failed');
        return res.text();
      })
      .then((xml) => {
        if (!alive) return;
        setTrack(parseGpxTrack(xml));
      })
      .catch((err) => {
        if (alive) {
          console.error('GpxTrackView: no se pudo cargar el GPX', err);
          setFailed(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [gpxFileId]);

  if (!gpxFileId || failed) return null;
  if (!track) return null; // still loading — the page just shows the rest without it

  return (
    <div className="Detail__section GpxTrack">
      <div className="Detail__sectionLabel">RECORRIDO</div>
      <div className="GpxTrack__body">
        <GpxMapPanel track={track} hoverIndex={hoverIndex} onHover={setHoverIndex} />
        {track.hasElevation && (
          <GpxProfilePanel track={track} hoverIndex={hoverIndex} onHover={setHoverIndex} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------- map panel ------------------------------- */

function GpxMapPanel({ track, hoverIndex, onHover }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const hoverMarkerRef = useRef(null);
  const glowPathRef = useRef(null);
  const linePathRef = useRef(null);
  const pointsRef = useRef(track.points);
  pointsRef.current = track.points;
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const points = track.points;
    const bounds = trackBounds(points);
    const mid = points[Math.floor(points.length / 2)];

    const map = new MaplibreMap({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          satellite: { type: 'raster', tiles: [SATELLITE_URL], tileSize: 256 },
          terrain: { type: 'raster-dem', tiles: [TERRAIN_URL], tileSize: 256, encoding: 'terrarium' },
        },
        layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
      },
      center: [mid.lon, mid.lat],
      zoom: 12,
      pitch: 55,
      bearing: -15,
      attributionControl: false,
      // 3D, orbitable: drag to rotate/tilt is on by default (drag with right
      // button, or two fingers on touch); we just add scroll-wheel zoom + the
      // compass/pitch control below so it's discoverable with a single click.
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ visualizePitch: true }), 'top-right');
    map.on('error', (e) => console.error('GpxTrackView map error', e?.error || e));

    // The route is drawn as a plain SVG overlay kept in sync with the camera,
    // not a GL line layer: a GL line depends on the tile-worker pipeline (the
    // same one that broke earlier with "Worker failed to load"), so it can
    // silently fail to render in some setups even once the map itself works.
    // Projecting points to screen pixels on every 'render' is exactly how
    // MapLibre positions its own DOM Markers, so it's guaranteed to track the
    // camera through pan/zoom/pitch/rotate.
    function updateRouteOverlay() {
      const pts = pointsRef.current;
      const d = pts
        .map((p, i) => {
          const { x, y } = map.project([p.lon, p.lat]);
          return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ');
      if (glowPathRef.current) glowPathRef.current.setAttribute('d', d);
      if (linePathRef.current) linePathRef.current.setAttribute('d', d);
    }

    map.on('load', () => {
      // Enabled here (the documented MapLibre pattern) rather than as a
      // declarative `terrain` key in the style JSON, which doesn't reliably
      // wire up elevation sampling in every version.
      map.setTerrain({ source: 'terrain', exaggeration: 1.4 });
      map.fitBounds(bounds, { padding: 48, pitch: 55, bearing: -15, duration: 0 });
      updateRouteOverlay();

      // Hover the map → find the nearest track point in screen space and report
      // it upward so the elevation profile can highlight the same point.
      map.on('mousemove', (e) => {
        const pts = pointsRef.current;
        let bestIdx = -1;
        let bestDist = Infinity;
        for (let i = 0; i < pts.length; i++) {
          const p = map.project([pts[i].lon, pts[i].lat]);
          const dx = p.x - e.point.x;
          const dy = p.y - e.point.y;
          const d = dx * dx + dy * dy;
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }
        onHoverRef.current(bestDist < 400 ? bestIdx : null); // 20px radius
      });
      map.on('mouseleave', () => onHoverRef.current(null));
    });
    map.on('render', updateRouteOverlay);
    map.on('resize', updateRouteOverlay);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect the hovered point (set from either panel) as a dot on the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (hoverMarkerRef.current) {
      hoverMarkerRef.current.remove();
      hoverMarkerRef.current = null;
    }
    if (hoverIndex == null) return;
    const p = track.points[hoverIndex];
    if (!p) return;
    const el = document.createElement('div');
    el.className = 'GpxTrack__hoverDot';
    hoverMarkerRef.current = new Marker({ element: el }).setLngLat([p.lon, p.lat]).addTo(map);
  }, [hoverIndex, track.points]);

  // Named waypoints (from <wpt> tags), if the GPX carries any.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !track.waypoints.length) return undefined;
    const markers = track.waypoints.map((w) => {
      const el = document.createElement('div');
      el.className = 'GpxTrack__wptFlag';
      el.innerHTML = `<strong>${w.name}</strong>`;
      return new Marker({ element: el, anchor: 'bottom' }).setLngLat([w.lon, w.lat]);
    });
    map.once('load', () => markers.forEach((m) => m.addTo(map)));
    if (map.loaded()) markers.forEach((m) => m.addTo(map));
    return () => markers.forEach((m) => m.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.waypoints]);

  return (
    <div className="GpxTrack__mapWrap">
      <div ref={containerRef} className="GpxTrack__map" aria-label="Mapa 3D del recorrido" />
      <svg className="GpxTrack__mapOverlay">
        <path ref={glowPathRef} fill="none" stroke="#F5B72A" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" opacity="0.45" style={{ filter: 'blur(4px)' }} />
        <path ref={linePathRef} fill="none" stroke="#F5B72A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/* ----------------------------- profile panel ------------------------------ */

// Left margin reserved for the altitude axis; the plotted area sits to its
// right within a viewBox that tracks the container's actual pixel width (see
// GpxProfilePanel) so 1 viewBox unit === 1 CSS px. Without that, `width:
// 100%` stretching a fixed 700-wide viewBox scales X and Y unevenly, which
// both blurs the hairline strokes and turns the hover dot into an ellipse.
const AXIS_W = 42;
const PAD_TOP = 28; // room for a waypoint's name above its dashed line
const CHART_H = 220;
const BASELINE_Y = 205;
const ELEVATION_TICK_STEP = 150; // metres
const MIN_LABEL_GAP = 10; // px between a grid tick and the start/end altitude label before it's dropped

function buildProfile(points, width) {
  const nums = points.map((p) => Number(p.ele)).filter(Number.isFinite);
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const span = max - min || 1;
  const plotW = width - AXIS_W;
  const plotH = BASELINE_Y - PAD_TOP;
  const stepX = plotW / (points.length - 1);
  const yFor = (ele) => +(BASELINE_Y - ((Number(ele) - min) / span) * plotH).toFixed(1);
  const pts = points.map((p, i) => [+(AXIS_W + i * stepX).toFixed(1), yFor(p.ele)]);
  const startY = pts[0][1];
  const endY = pts[pts.length - 1][1];

  // Skip a regular grid tick whenever it would land right on top of the
  // start/end altitude label — keep only the (bold, amber) edge label there.
  const ticks = [];
  const firstTick = Math.ceil(min / ELEVATION_TICK_STEP) * ELEVATION_TICK_STEP;
  for (let v = firstTick; v <= max; v += ELEVATION_TICK_STEP) {
    const y = yFor(v);
    if (Math.abs(y - startY) < MIN_LABEL_GAP || Math.abs(y - endY) < MIN_LABEL_GAP) continue;
    ticks.push({ value: v, y });
  }

  return { pts, min, max, ticks, startEle: Math.round(points[0].ele), endEle: Math.round(points[points.length - 1].ele) };
}

// Builds the "M.. L.." path string for a run of already-projected [x,y] points.
function pathFor(pts) {
  return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ');
}

function GpxProfilePanel({ track, hoverIndex, onHover }) {
  const svgRef = useRef(null);
  const [width, setWidth] = useState(700); // measured below; this default only matters for the first frame

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const profile = useMemo(() => buildProfile(track.points, width), [track.points, width]);
  const n = track.points.length;

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const ratioX = (e.clientX - rect.left) * (width / rect.width);
    const ratio = Math.min(1, Math.max(0, (ratioX - AXIS_W) / (width - AXIS_W)));
    onHover(Math.round(ratio * (n - 1)));
  };

  const hoverPt = hoverIndex != null ? profile.pts[hoverIndex] : null;
  // Only the walked portion (start → hovered point) lights up amber; the rest
  // of the profile stays a dim, unwalked gray until it's hovered too.
  const walkedPts = hoverIndex != null ? profile.pts.slice(0, hoverIndex + 1) : null;

  return (
    <svg
      ref={svgRef}
      className="GpxTrack__profile"
      viewBox={`0 0 ${width} ${CHART_H}`}
      width="100%"
      height={CHART_H}
      fill="none"
      onMouseMove={handleMove}
      onMouseLeave={() => onHover(null)}
    >
      <defs>
        <linearGradient id="gpxWalkedFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5B72A" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#F5B72A" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Altitude axis: a horizontal gridline + label every 150 m. */}
      {profile.ticks.map((t) => (
        <g key={t.value}>
          <line x1={AXIS_W} y1={t.y} x2={width} y2={t.y} stroke="#2E4A38" strokeWidth="1" />
          <text x={AXIS_W - 6} y={t.y} textAnchor="end" dominantBaseline="middle" className="GpxTrack__axisLabel">
            {t.value}
          </text>
        </g>
      ))}

      {/* Full route, dim until walked. */}
      <path d={pathFor(profile.pts)} stroke="#4C5D53" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <path d={`M${AXIS_W} ${BASELINE_Y} L${width} ${BASELINE_Y}`} stroke="#2E4A38" strokeWidth="1" />

      {/* Walked portion: amber line + gradient fill down to the baseline. */}
      {walkedPts && walkedPts.length > 1 && (
        <>
          <path
            d={`${pathFor(walkedPts)} L${walkedPts[walkedPts.length - 1][0]} ${BASELINE_Y} L${AXIS_W} ${BASELINE_Y} Z`}
            fill="url(#gpxWalkedFill)"
            stroke="none"
          />
          <path d={pathFor(walkedPts)} stroke="#F5B72A" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        </>
      )}

      {track.waypoints.map((w) => {
        const x = AXIS_W + (w.index / (n - 1)) * (width - AXIS_W);
        return (
          <g key={w.name + w.index}>
            <line x1={x} y1={PAD_TOP - 8} x2={x} y2={BASELINE_Y} stroke="#F5B72A" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            <text x={x} y={PAD_TOP - 12} textAnchor="middle" className="GpxTrack__wptLabel">
              {w.name}
            </text>
          </g>
        );
      })}

      {/* Start / end altitude, pinned to the axis at their own elevation. When
          a loop brings the end back close to the start, the start label wins
          and the end one is dropped rather than overlapping it. */}
      <text x={AXIS_W - 6} y={profile.pts[0][1]} textAnchor="end" dominantBaseline="middle" className="GpxTrack__axisLabel GpxTrack__axisLabel--edge">
        {profile.startEle}
      </text>
      {Math.abs(profile.pts[n - 1][1] - profile.pts[0][1]) >= MIN_LABEL_GAP && (
        <text x={AXIS_W - 6} y={profile.pts[n - 1][1]} textAnchor="end" dominantBaseline="middle" className="GpxTrack__axisLabel GpxTrack__axisLabel--edge">
          {profile.endEle}
        </text>
      )}

      {hoverPt && <circle cx={hoverPt[0]} cy={hoverPt[1]} r="4.5" fill="#FFFFFF" stroke="#16281E" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

export default GpxTrackView;
